import { useState } from "react";
import { useLocation } from "wouter";
import { AlertTriangle, Check, FileSignature, Loader2, ShieldAlert } from "lucide-react";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { CreateContactModal, type Contact } from "@/components/ContactSelector";

type Candidate = {
  id?: string;
  name?: string;
  companyName?: string;
  nomCommercial?: string;
  organisationType?: string;
  reason?: string;
  matchReason?: string;
};

type ExtractedContact = {
  prenom?: string | null;
  nom?: string | null;
  poste?: string | null;
  email?: string | null;
  linkedin_url?: string | null;
  telephones?: Array<{ libelle: string; numero: string; principal: boolean }>;
  societe_texte?: string | null;
  service?: string | null;
  adresse_brute?: string | null;
  organisation_type?: string | null;
  champs_incertains?: string[];
};

export function ContactSignatureImport({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: (contact: Contact) => void;
}) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [step, setStep] = useState<"paste" | "review">("paste");
  const [text, setText] = useState("");
  const [instruction, setInstruction] = useState("");
  const [result, setResult] = useState<ExtractedContact | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("none");
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [textTruncated, setTextTruncated] = useState(false);
  const [extractionReceipt, setExtractionReceipt] = useState("");

  const reset = () => {
    setStep("paste"); setText(""); setInstruction(""); setResult(null);
    setCandidates([]); setSelectedCompany("none"); setDuplicates([]);
    setLoading(false); setCreateOpen(false); setTextTruncated(false); setExtractionReceipt("");
  };
  const close = () => { reset(); onClose(); };

  const extract = async () => {
    if (text.trim().length < 10) return;
    setLoading(true);
    try {
      const res = await authFetch("/api/assistant/contacts/extract", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ texte: text, ...(instruction.trim() ? { consigne: instruction.trim() } : {}) }),
      });
      if (!res.ok) {
        const errorBody = await res.json().catch(() => null);
        throw new Error(errorBody?.message || "Extraction impossible");
      }
      const data = await res.json();
      setResult(data.contact || {});
      setCandidates(Array.isArray(data.societes_candidates) ? data.societes_candidates : []);
      setDuplicates(Array.isArray(data.doublons) ? data.doublons : []);
      setTextTruncated(data.texte_tronque === true);
      setExtractionReceipt(typeof data.extraction_receipt === "string" ? data.extraction_receipt : "");
      setStep("review");
    } catch (error: any) {
      toast({ title: "Extraction impossible", description: error.message, variant: "destructive" });
    } finally { setLoading(false); }
  };

  const selectedCandidate = selectedCompany === "none" ? null : candidates.find((c, i) => (c.id || `candidate-${i}`) === selectedCompany);
  const uncertain = result?.champs_incertains || [];
  const duplicateContact = (d: any) => d.contact || d;
  const isStrongDuplicate = (d: any) => d.fort || d.type === "fort" || d.type === "email_exact" || d.niveau === "fort" || d.force === "fort" || d.emailMatch;
  const hasStrongDuplicate = duplicates.some(isStrongDuplicate);
  const initialValues = result ? {
    prenom: result.prenom || "",
    nom: result.nom || "",
    poste: result.poste || "",
    email: result.email || "",
    telephone: result.telephones?.find(t => t.principal)?.numero || result.telephones?.[0]?.numero || "",
    telephones: result.telephones || [],
    organisationType: selectedCandidate?.organisationType || result.organisation_type || "",
    organisationId: selectedCandidate?.id || null,
    organisationLibre: selectedCandidate ? (selectedCandidate.name || selectedCandidate.companyName || selectedCandidate.nomCommercial || "") : (result.societe_texte || ""),
    notes: [result.service && `Service : ${result.service}`, result.adresse_brute && `Adresse : ${result.adresse_brute}`].filter(Boolean).join("\n"),
    linkedin: result.linkedin_url || "",
    source: "extraction_signature",
    extractionReceipt,
  } : undefined;

  return (
    <>
      <Dialog open={open && !createOpen} onOpenChange={v => { if (!v) close(); }}>
        <DialogContent className="w-[calc(100%-1rem)] max-w-2xl max-h-[min(860px,calc(100dvh-1rem))] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-primary" />Coller un contact</DialogTitle>
            <DialogDescription>
              {step === "paste" ? "Collez une signature email. Rien n'est créé avant votre relecture." : "Vérifiez chaque valeur avant de créer la fiche."}
            </DialogDescription>
          </DialogHeader>

          {step === "paste" ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm text-muted-foreground">
                L'extraction propose des valeurs à partir du texte collé, sans modifier l'annuaire.
              </div>
              <div className="space-y-2">
                <Label htmlFor="signature-text">Signature à analyser <span className="text-destructive">*</span></Label>
                <Textarea id="signature-text" value={text} onChange={e => setText(e.target.value)} rows={9} placeholder={"Marie Martin\nDirectrice commerciale\nACME Industries\n+33 6 12 34 56 78\nmarie.martin@acme.fr"} data-testid="textarea-signature" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signature-instruction">Instruction complémentaire <span className="text-muted-foreground">(optionnel)</span></Label>
                <Textarea id="signature-instruction" value={instruction} onChange={e => setInstruction(e.target.value)} rows={2} placeholder="Ex. privilégier le numéro mobile" data-testid="textarea-signature-instruction" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={close}>Annuler</Button>
                <Button onClick={extract} disabled={text.trim().length < 10 || loading} data-testid="button-extract-contact">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSignature className="mr-2 h-4 w-4" />}
                  Extraire les informations
                </Button>
              </DialogFooter>
            </div>
          ) : result && (
            <div className="space-y-4">
              {hasStrongDuplicate && (
                <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3 text-red-900 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200" role="alert">
                  <div className="flex gap-2"><ShieldAlert className="h-5 w-5 shrink-0" /><div><p className="font-semibold">Un contact existant correspond exactement</p><p className="mt-0.5 text-sm">La création est remplacée par l'ouverture de la fiche existante.</p></div></div>
                  <div className="mt-2 space-y-1 pl-7 text-sm">{duplicates.filter(isStrongDuplicate).map((d, i) => { const c = duplicateContact(d); return <p key={c.id || i}>{c.prenom} {c.nom}{c.email ? ` — ${c.email}` : ""}</p>; })}</div>
                  <Button className="mt-3 ml-7 bg-red-700 text-white hover:bg-red-800" onClick={() => {
                    const id = duplicateContact(duplicates.find(isStrongDuplicate))?.id; if (id) { close(); navigate(`/contacts/${id}`); }
                  }}>Ouvrir la fiche existante</Button>
                </div>
              )}
              <div className="grid gap-3 rounded-lg border bg-muted/20 p-3 sm:grid-cols-2">
                {[
                  ["Prénom", result.prenom], ["Nom", result.nom], ["Poste", result.poste],
                  ["Email", result.email], ["Téléphone", result.telephones?.[0]?.numero], ["Société", result.societe_texte],
                ].map(([label, value]) => <div key={label} className={`min-w-0 rounded-md border p-2 ${uncertain.some(u => u.toLowerCase().includes(String(label).toLowerCase())) ? "border-amber-400 bg-amber-50/70 dark:bg-amber-950/20" : "bg-background"}`}>
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p><p className="truncate text-sm">{value || "Non renseigné"}</p>
                  {uncertain.some(u => u.toLowerCase().includes(String(label).toLowerCase())) && <Badge className="mt-1 border-amber-300 bg-amber-100 text-amber-900 hover:bg-amber-100">À vérifier</Badge>}
                </div>)}
              </div>
              {uncertain.length > 0 && <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/20"><AlertTriangle className="h-4 w-4 shrink-0" /><span>Valeurs incertaines : {uncertain.join(", ")}. Elles restent modifiables dans le formulaire.</span></div>}
              {textTruncated && <div className="flex gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/20"><AlertTriangle className="h-4 w-4 shrink-0" /><span>Le texte dépassait 5 000 caractères. Seuls les 5 000 premiers ont été analysés.</span></div>}
              <Separator />
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Rattacher à une société <span className="text-muted-foreground">(optionnel)</span></legend>
                <div className="space-y-2">
                  <label className="flex cursor-pointer items-start gap-2 rounded-md border p-2.5 hover:bg-muted/40"><input type="radio" name="company-candidate" value="none" checked={selectedCompany === "none"} onChange={() => setSelectedCompany("none")} className="mt-0.5" /><span><span className="text-sm font-medium">Ne pas rattacher</span><span className="block text-xs text-muted-foreground">{result.societe_texte ? `Aucune correspondance fiable pour « ${result.societe_texte} »` : "Aucune société identifiée"}</span></span></label>
                  {candidates.map((c, i) => { const id = c.id || `candidate-${i}`; return <label key={id} className="flex cursor-pointer items-start gap-2 rounded-md border p-2.5 hover:bg-muted/40"><input type="radio" name="company-candidate" value={id} checked={selectedCompany === id} onChange={() => setSelectedCompany(id)} className="mt-0.5" /><span><span className="text-sm font-medium">{c.name || c.companyName || c.nomCommercial || "Société proposée"}</span><span className="block text-xs text-muted-foreground">{c.reason || c.matchReason || "Correspondance proposée"}</span></span></label>; })}
                </div>
              </fieldset>
              <DialogFooter className="gap-2 sm:justify-between">
                <Button variant="ghost" onClick={() => setStep("paste")}>Modifier le texte</Button>
                {!hasStrongDuplicate && <Button onClick={() => setCreateOpen(true)}><Check className="mr-2 h-4 w-4" />Revoir et créer</Button>}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {initialValues && <CreateContactModal open={createOpen} onClose={() => setCreateOpen(false)} initialValues={initialValues} uncertainFields={uncertain} onCreated={contact => { onCreated(contact); close(); }} />}
    </>
  );
}